import { useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type Ref, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { NavLink } from "react-router";

type ButtonVariant = "primary" | "secondary" | "danger";

export function Button({
  variant = "secondary",
  small,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; small?: boolean; ref?: Ref<HTMLButtonElement> }) {
  const classes = ["sb-button", `sb-button--${variant}`, small ? "sb-button--small" : "", className ?? ""].join(" ").trim();
  return <button type="button" className={classes} {...props} />;
}

export function ButtonLink({ href, variant = "secondary", children }: { href: string; variant?: ButtonVariant; children: ReactNode }) {
  return (
    <a className={`sb-button sb-button--${variant}`} href={href}>
      {children}
    </a>
  );
}

interface FieldProps {
  label: string;
  hint?: string;
  error?: string | undefined;
}

export function TextField({ label, hint, error, id, ...input }: FieldProps & InputHTMLAttributes<HTMLInputElement>) {
  const generated = useId();
  const inputId = id ?? generated;
  return (
    <div className="sb-field">
      <label htmlFor={inputId}>{label}</label>
      <input
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(inputId, hint, error)}
        {...input}
      />
      {hint && <span id={`${inputId}-hint`} className="sb-hint">{hint}</span>}
      {error && <span id={`${inputId}-error`} className="sb-error">{error}</span>}
    </div>
  );
}

function describedBy(id: string, hint: unknown, error: unknown): string | undefined {
  const ids = [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean);
  return ids.length ? ids.join(" ") : undefined;
}

export function TextArea({ label, hint, error, id, ...input }: FieldProps & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const generated = useId();
  const inputId = id ?? generated;
  return (
    <div className="sb-field">
      <label htmlFor={inputId}>{label}</label>
      <textarea id={inputId} aria-invalid={error ? true : undefined} aria-describedby={describedBy(inputId, hint, error)} {...input} />
      {hint && <span id={`${inputId}-hint`} className="sb-hint">{hint}</span>}
      {error && <span id={`${inputId}-error`} className="sb-error">{error}</span>}
    </div>
  );
}

export function SelectField({ label, hint, error, id, children, ...select }: FieldProps & SelectHTMLAttributes<HTMLSelectElement>) {
  const generated = useId();
  const inputId = id ?? generated;
  return (
    <div className="sb-field">
      <label htmlFor={inputId}>{label}</label>
      <select id={inputId} aria-invalid={error ? true : undefined} aria-describedby={describedBy(inputId, hint, error)} {...select}>
        {children}
      </select>
      {hint && <span id={`${inputId}-hint`} className="sb-hint">{hint}</span>}
      {error && <span id={`${inputId}-error`} className="sb-error">{error}</span>}
    </div>
  );
}

export function Card({ title, description, actions, children, labelledBy }: { title?: string; description?: string; actions?: ReactNode; children: ReactNode; labelledBy?: string }) {
  const generated = useId();
  const headingId = labelledBy ?? generated;
  return (
    <section className="sb-card" aria-labelledby={title ? headingId : undefined}>
      {(title || actions) && (
        <div className="sb-card-header">
          <div>
            {title && <h2 id={headingId}>{title}</h2>}
            {description && <p>{description}</p>}
          </div>
          {actions}
        </div>
      )}
      <div className="sb-card-body">{children}</div>
    </section>
  );
}

export function PageHeader({ title, description, actions }: { title: string; description?: string; actions?: ReactNode }) {
  return (
    <div className="sb-page-header">
      <div>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions}
    </div>
  );
}

export type Tone = "neutral" | "success" | "warning" | "danger" | "accent";

export function Badge({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`sb-badge sb-badge--${tone}`}>{children}</span>;
}

/** `live={false}` when focus is moved onto the message anyway: a live region would be announced a second time. */
export function Alert({ tone = "info", live = true, children }: { tone?: "info" | "success" | "danger" | "warning"; live?: boolean; children: ReactNode }) {
  return (
    <div className={`sb-alert sb-alert--${tone}`} role={live ? (tone === "danger" ? "alert" : "status") : undefined}>
      {children}
    </div>
  );
}

export function DescriptionList({ items }: { items: [label: string, value: ReactNode][] }) {
  return (
    <dl className="sb-dl">
      {items.map(([label, value]) => (
        <div key={label} style={{ display: "contents" }}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Table({ caption, headers, children }: { caption: string; headers: string[]; children: ReactNode }) {
  return (
    <div className="sb-table-wrap">
      <table className="sb-table">
        <caption>{caption}</caption>
        <thead>
          <tr>
            {headers.map((h) => (
              <th key={h} scope="col">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return (
    <p className="sb-muted" role="status" aria-live="polite">
      {label}
    </p>
  );
}

export interface NavItem {
  to: string;
  label: string;
}

export function AppShell({
  surfaceLabel,
  nav,
  userLabel,
  logoutUrl,
  children,
}: {
  surfaceLabel: string;
  nav: NavItem[];
  userLabel: string;
  logoutUrl: string;
  children: ReactNode;
}) {
  return (
    <>
      <a className="sb-skip" href="#main">
        Skip to content
      </a>
      <header className="sb-header">
        <div className="sb-header-inner">
          <NavLink to="/" className="sb-brand">
            Doubleslash Bookings <span className="sb-brand-surface">{surfaceLabel}</span>
          </NavLink>
          <nav className="sb-nav" aria-label="Main">
            {nav.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === "/"}>
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="sb-user">
            <span data-testid="signed-in-user">{userLabel}</span>
            <a className="sb-button sb-button--secondary sb-button--small" href={logoutUrl}>
              Sign out
            </a>
          </div>
        </div>
      </header>
      <main id="main" className="sb-main" tabIndex={-1}>
        {children}
      </main>
    </>
  );
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/**
 * A destructive action with a confirm step: the trigger button swaps for a warning with Confirm and Cancel. Focus goes
 * to Cancel (the safe choice; Escape cancels too), both buttons are described by the warning so screen readers read it,
 * and focus returns to the trigger afterwards. While the action runs the trigger is disabled, also when the caller passes
 * no `pending` (F14, A20.1, A20.L1, A20.L2).
 */
export function ConfirmAction({
  label,
  confirmLabel,
  message,
  onConfirm,
  pending,
  dismissLabel = "Cancel",
}: {
  label: string;
  confirmLabel: string;
  /** The button that keeps things as they are; name it when "Cancel" is ambiguous (e.g. "Keep booking", E7.12). */
  dismissLabel?: string;
  message: ReactNode;
  onConfirm: () => void | Promise<void>;
  pending?: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const busy = pending || running;
  const returnFocus = useRef(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const messageId = useId();
  // Back to the trigger once it is enabled again (it is disabled while the action runs), then never again by itself.
  useEffect(() => {
    if (confirming) {
      cancelRef.current?.focus();
    } else if (returnFocus.current && !busy) {
      triggerRef.current?.focus();
      returnFocus.current = false;
    }
  }, [confirming, busy]);

  const close = () => {
    returnFocus.current = true;
    setConfirming(false);
  };

  if (!confirming) {
    return (
      <Button ref={triggerRef} variant="danger" onClick={() => setConfirming(true)} disabled={busy}>
        {label}
      </Button>
    );
  }

  return (
    <Alert tone="danger" live={false}>
      <div onKeyDown={(e) => e.key === "Escape" && close()}>
        <div id={messageId}>{message}</div>
        <div className="sb-actions" style={{ justifyContent: "flex-start", marginTop: 8 }}>
          <Button
            variant="danger"
            aria-describedby={messageId}
            disabled={busy}
            onClick={async () => {
              close();
              setRunning(true);
              try {
                await onConfirm();
              } catch (error) {
                // Callers show their own errors (useCommand); a rejection must not escape as an unhandled one (A20.R7).
                console.error(error);
              } finally {
                setRunning(false);
              }
            }}
          >
            {confirmLabel}
          </Button>
          <Button ref={cancelRef} aria-describedby={messageId} onClick={close}>
            {dismissLabel}
          </Button>
        </div>
      </div>
    </Alert>
  );
}
